import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useToast } from "../context/ToastContext";
import { getSettingsBranding, saveBranding } from "../api";

const NAVY = "#1e3a8a";

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 };

/**
 * People Settings — configuration owned by the People (Core) module.
 *
 * These cards used to render at the bottom of Settings > Branding, which is why
 * nobody could find them: an attendance photo toggle has no reason to live under
 * "Branding". Recruitment already solved this the same way (intake config moved
 * to Recruiter Preferences); this is the People equivalent.
 *
 * Persistence still goes through the branding settings endpoint — it is the
 * generic tenant-settings writer, not a branding-specific one.
 */
export default function PeopleSettingsPage() {
  return (
    <div style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div>
          <h1>People Settings</h1>
          <p>Attendance capture and timesheet configuration for your workspace</p>
        </div>
      </div>
      <AttendanceGeoCard />
      <TimesheetCard />
    </div>
  );
}

function AttendanceGeoCard() {
  const { toast } = useToast();
  const { data: branding } = useQuery({ queryKey: ["settings-branding"], queryFn: getSettingsBranding });
  const [form, setForm] = useState({ attendance_geo_enabled: "false", attendance_geo_mode: "capture", attendance_office_name: "", attendance_office_lat: "", attendance_office_lng: "", attendance_office_radius: "200", attendance_require_punch_photo: "false" });
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (branding) setForm({
      attendance_geo_enabled: (branding as any).attendance_geo_enabled || "false",
      attendance_geo_mode: (branding as any).attendance_geo_mode || "capture",
      attendance_office_name: (branding as any).attendance_office_name || "",
      attendance_office_lat: (branding as any).attendance_office_lat || "",
      attendance_office_lng: (branding as any).attendance_office_lng || "",
      attendance_office_radius: (branding as any).attendance_office_radius || "200",
      attendance_require_punch_photo: (branding as any).attendance_require_punch_photo || "false",
    });
  }, [branding]);

  const saveMut = useMutation({
    mutationFn: () => saveBranding(form),
    onSuccess: () => { setSaved(true); toast("Attendance settings saved", "success"); setTimeout(() => setSaved(false), 3000); },
    onError: () => toast("Failed to save", "error"),
  });

  const enabled = form.attendance_geo_enabled === "true";

  return (
    <div style={{ background: "white", borderRadius: 12, padding: "24px 28px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginTop: 20 }}>
      <LoadingOverlay active={saveMut.isPending} />
      <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 4 }}>📍 Attendance Geolocation</div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}>Optional: capture employee location on check-in/out. No Google API required.</div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Enable geolocation</label>
        <input type="checkbox" checked={enabled} onChange={e => setForm(f => ({ ...f, attendance_geo_enabled: e.target.checked ? "true" : "false" }))} style={{ width: 18, height: 18, cursor: "pointer" }} />
      </div>

      {enabled && (
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Mode</label>
            <select value={form.attendance_geo_mode} onChange={e => setForm(f => ({ ...f, attendance_geo_mode: e.target.value }))}
              style={{ padding: "8px 12px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 13, width: "100%", maxWidth: 240, boxSizing: "border-box" }}>
              <option value="capture">Capture only (visible to managers, not enforced)</option>
              <option value="enforce">Enforce (flag outside-office punches for review)</option>
            </select>
          </div>
          <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 100px", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Office Name</label>
              <input value={form.attendance_office_name} onChange={e => setForm(f => ({ ...f, attendance_office_name: e.target.value }))} placeholder="Head Office" style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box" as const }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Latitude</label>
              <input value={form.attendance_office_lat} onChange={e => setForm(f => ({ ...f, attendance_office_lat: e.target.value }))} placeholder="13.7563" style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box" as const }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Longitude</label>
              <input value={form.attendance_office_lng} onChange={e => setForm(f => ({ ...f, attendance_office_lng: e.target.value }))} placeholder="100.5018" style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box" as const }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Radius (m)</label>
              <input type="number" value={form.attendance_office_radius} onChange={e => setForm(f => ({ ...f, attendance_office_radius: e.target.value }))} style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box" as const }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>
            To get coordinates: open <a href="https://maps.google.com" target="_blank" rel="noreferrer" style={{ color: NAVY }}>Google Maps</a>, right-click your office → the coordinates are shown at the top.
          </div>
        </>
      )}

      <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>📸 Require photo on check-in/out</label>
          <input type="checkbox" checked={form.attendance_require_punch_photo === "true"}
            onChange={e => setForm(f => ({ ...f, attendance_require_punch_photo: e.target.checked ? "true" : "false" }))}
            style={{ width: 18, height: 18, cursor: "pointer" }} />
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
          Employees must take a selfie when punching. Photos are visible to admins, HR, and finance on the team attendance view and correction reviews.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} style={{ background: NAVY, color: "white", border: "none", padding: "9px 22px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
          {saveMut.isPending ? "Saving…" : "Save"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
      </div>
    </div>
  );
}

function TimesheetCard() {
  const { toast } = useToast();
  const { data: branding } = useQuery({ queryKey: ["settings-branding"], queryFn: getSettingsBranding });
  const [enabled, setEnabled] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (branding) setEnabled((branding as any).timesheet_enabled === "true");
  }, [branding]);

  const saveMut = useMutation({
    mutationFn: () => saveBranding({ timesheet_enabled: enabled ? "true" : "false" }),
    onSuccess: () => { setSaved(true); toast("Timesheet setting saved", "success"); setTimeout(() => setSaved(false), 3000); },
    onError: () => toast("Failed to save", "error"),
  });

  return (
    <div style={{ background: "white", borderRadius: 12, padding: "24px 28px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginTop: 20 }}>
      <LoadingOverlay active={saveMut.isPending} />
      <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 4 }}>⏱ Timesheet (Time Allocation)</div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}>
        When enabled, employees can log hours per project on their Attendance page. Managers can view team time allocation.
        Best suited for IT / staffing tenants billing by the hour.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Enable timesheet feature</label>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} style={{ background: NAVY, color: "white", border: "none", padding: "9px 22px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
          {saveMut.isPending ? "Saving…" : "Save"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
      </div>
    </div>
  );
}
